import { useEffect, useState } from "react";
import { useAccount, usePublicClient } from "wagmi";
import { erc20Abi, formatUnits } from "viem";
import { EXPLORER } from "./chains";

const CHUNK = 250;

// Reads the connected wallet's balances on Robinhood Chain: native ETH + balanceOf for every known token (Multicall3),
// plus best-effort discovery of other held tokens via the Blockscout API.
export function useWalletHoldings(tokens) {
  const { address } = useAccount();
  const pc = usePublicClient({ chainId: 4663 });
  const [state, setState] = useState({ balances: {}, extra: [] });
  const key = tokens.length;

  useEffect(() => {
    if (!address || !pc || !tokens.length) return setState({ balances: {}, extra: [] });
    let live = true;
    const run = async () => {
      const erc = tokens.filter((t) => t.address !== "native");
      const balances = {};
      try { balances.native = await pc.getBalance({ address }); } catch { /* rpc hiccup */ }
      for (let i = 0; i < erc.length; i += CHUNK) {
        const part = erc.slice(i, i + CHUNK);
        try {
          const res = await pc.multicall({ contracts: part.map((t) => ({ address: t.address, abi: erc20Abi, functionName: "balanceOf", args: [address] })), allowFailure: true });
          res.forEach((r, j) => { if (r.status === "success" && r.result > 0n) balances[part[j].address.toLowerCase()] = r.result; });
        } catch { /* skip chunk */ }
      }
      let extra = [];
      try {
        const r = await fetch(`${EXPLORER}/api/v2/addresses/${address}/token-balances`);
        if (r.ok) {
          const known = new Set(erc.map((t) => t.address.toLowerCase()));
          extra = (await r.json()).filter((b) => b.token?.type === "ERC-20" && BigInt(b.value || 0) > 0n && !known.has(b.token.address_hash?.toLowerCase() || b.token.address?.toLowerCase()))
            .map((b) => {
              const a = (b.token.address_hash || b.token.address).toLowerCase();
              balances[a] = BigInt(b.value);
              return { address: a, symbol: (b.token.symbol || "?").slice(0, 24), name: b.token.name || "", decimals: Number(b.token.decimals || 18), logo: b.token.icon_url, verified: false, kind: "crypto", fromWallet: true };
            });
        }
      } catch { /* explorer API unavailable from this browser */ }
      live && setState({ balances, extra });
    };
    run();
    const id = setInterval(run, 45000);
    return () => { live = false; clearInterval(id); };
  }, [address, pc, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

export const sortByHoldings = (tokens, balances, ethUsd) => {
  const bal = (t) => balances[t.address === "native" ? "native" : t.address.toLowerCase()];
  const usd = (t) => {
    const b = bal(t);
    if (!b) return -1;
    const amt = Number(formatUnits(b, t.decimals ?? 18));
    const px = t.address === "native" ? ethUsd : t.price_usd;
    return px ? amt * px : amt * 1e-9;
  };
  const withBal = tokens.map((t) => (bal(t) ? { ...t, balance: formatUnits(bal(t), t.decimals ?? 18), held: true } : t));
  const held = withBal.filter((t) => t.held).sort((a, b) => usd(b) - usd(a));
  return [...held, ...withBal.filter((t) => !t.held)];
};
